import { useParams } from 'react-router-dom';
import { Card, Spinner } from 'reactstrap';
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor";
import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import DataLoader from '../../../components/loader/DataLoader';
import DataLoaderFailed from '../../../components/loader/DataLoaderFailed';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { fetchRevisionAdditionalInformation, saveRevisionAdditionalInformationResult, saveRevisionAdditionalInformationKeepalive } from '../data/quoteRevisionService';
import { createAudit } from '../../audit/data/auditService';
import { useDispatch, useSelector } from 'react-redux';
import { setDraftValue, setServerValue, clearDraft, coerceToString } from '../data/quoteRevisionNotesSlice';
import { toast } from 'react-toastify';
import { useLockState } from '../../../components/userLocks/LockCheck';
import debounce from 'lodash/debounce';

export default function QuoteRevisionNotes({onRevisionNotesChanged }) {
    const { id: opportunityId, quoteId, quoteRevisionId } = useParams();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const lockState = useLockState();
    const draftEntry = useSelector(state => state.quoteRevisionNotes?.drafts?.[quoteRevisionId] || { value: "", dirty: false });
    const value = draftEntry.value ?? "";
    const isDirty = !!draftEntry.dirty;
    const [saveStatus, setSaveStatus] = useState(null); // null | "saving" | "saved" | "error"

    // ---- Race/loop guards (mirroring QuoteContent's approach) ----
    const lastSentRef = useRef("");       // dedupe: skip if value hasn't changed
    const pendingSaveRef = useRef(null);  // queue latest value while a save is in-flight

    const { data, isLoading, isError } = useQuery({
        queryKey: ["quoteRevisionAdditionalInformation", quoteRevisionId],
        queryFn: () => fetchRevisionAdditionalInformation(quoteRevisionId),
        staleTime: 60000,
    });

    // ------------------- Mutation (replaces manual async/ref loop) -------------------
    const saveMutation = useMutation({
        mutationFn: async (val) => {
            if (onRevisionNotesChanged) {
                // If parent passed a callback, assume it's handling persistence and just notify it of changes
                onRevisionNotesChanged?.(val);
            } else {
                // Otherwise, do the save here and keep query cache in sync ourselves
                const res = await saveRevisionAdditionalInformationResult(quoteRevisionId, val);
                if (!res?.ok) throw new Error("Save failed");
            }

            return val;
        },
        onMutate: () => setSaveStatus("saving"),
        onSuccess: (savedVal) => {
            // Keep query cache in sync so tab-switch remount doesn't show stale data
            queryClient.setQueryData(
                ["quoteRevisionAdditionalInformation", quoteRevisionId],
                savedVal
            );
            dispatch(clearDraft({ revisionId: quoteRevisionId, savedValue: savedVal }));


            // Fire-and-forget audit; can't delay persistence
            void createAudit({
                action: "Quote additional information changed for revision: " + quoteRevisionId,
                result: "Successful",
                source: `quote.${quoteId}`,
                payload: JSON.stringify(savedVal, null, 2)
            }).catch(() => {/* ignore audit failures */});

            // If something queued up while this save was in-flight, stay in "saving"
            if (pendingSaveRef.current !== null) {
                setSaveStatus("saving");
            } else {
                setTimeout(() => setSaveStatus("saved"), 1000);
                setTimeout(() => setSaveStatus(null), 1200);
            }
        },
        onError: () => {
            toast.error("Failed to save revision notes. Your latest changes have not been saved. Please try again.");
            setSaveStatus("error");
        },
    });

    // ------------------- Save orchestration (mirrors QuoteContent's requestSave) -------------------
    const requestSave = useCallback((val) => {
        if (!quoteRevisionId) return;

        if (saveMutation.isPending) {
            pendingSaveRef.current = val;
            return;
        }

        saveMutation.mutate(val);
    }, [quoteRevisionId, saveMutation]);

    // Drain the pending queue once the in-flight save completes
    useEffect(() => {
        if (saveMutation.isPending) return;
        if (pendingSaveRef.current === null) return;

        const next = pendingSaveRef.current;
        pendingSaveRef.current = null;
        lastSentRef.current = next;
        requestSave(next);
    }, [saveMutation.isPending, requestSave]);

    const maybeSave = useCallback((val) => {
        const snapshot = coerceToString(val);
        if (snapshot === lastSentRef.current) return; // dedupe: nothing changed
        lastSentRef.current = snapshot;
        requestSave(snapshot);
    }, [requestSave]);

    // Stable debounce created once; calls through a ref so it always uses latest maybeSave
    const maybeSaveRef = useRef(maybeSave);
    useEffect(() => { maybeSaveRef.current = maybeSave; }, [maybeSave]);

    const debouncedSave = useMemo(
        () => debounce((val) => maybeSaveRef.current(val), 500),
        [] // created once for the lifetime of the component
    );
    useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

    // Autosave while typing
    useEffect(() => {
        if (!isDirty) return;
        setSaveStatus("saving"); // optimistic UI feedback
        debouncedSave(value);
    }, [value, isDirty, debouncedSave]);

    // ------------------- Leave/unload best-effort save -------------------
    const latestValueRef = useRef(value);
    const dirtyRef = useRef(isDirty);
    const unloadingRef = useRef(false);
    useEffect(() => { latestValueRef.current = value; }, [value]);
    useEffect(() => { dirtyRef.current = isDirty; }, [isDirty]);

    useEffect(() => {
        const trySave = () => {
            if (dirtyRef.current) {
                unloadingRef.current = true;
                saveRevisionAdditionalInformationKeepalive(quoteRevisionId, coerceToString(latestValueRef.current));
            }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') trySave();
        };

        window.addEventListener('pagehide', trySave);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            window.removeEventListener('pagehide', trySave);
            document.removeEventListener('visibilitychange', onVisibilityChange);

            if (!dirtyRef.current) return;

            if (!unloadingRef.current && document.visibilityState !== 'hidden') {
                // In-app navigation: do a reliable normal save
                void saveMutation.mutateAsync(coerceToString(latestValueRef.current)).catch(() => {});
            } else {
                saveRevisionAdditionalInformationKeepalive(quoteRevisionId, coerceToString(latestValueRef.current));
            }
        };
    }, [quoteRevisionId]); // stable � intentionally excludes saveMutation to avoid re-registering on every render

    // ------------------- Sync server data into Redux -------------------
    useEffect(() => {
        if (data !== undefined) {
            dispatch(setServerValue({ revisionId: quoteRevisionId, value: data }));
        }
    }, [data, dispatch, quoteRevisionId]);

    const handleChange = useCallback((v) => {
        const next = coerceToString(v);
        if (next !== value) {
            dispatch(setDraftValue({ revisionId: quoteRevisionId, value: next }));
        }
    }, [dispatch, quoteRevisionId, value]);

    if (isLoading) return <DataLoader />;
    if (isError) return <DataLoaderFailed />;

    return (
        <Card className="h-100">
            <div className="d-flex justify-content-end align-items-center gap-2 me-3 mt-2">
                {saveStatus === null && (
                    <span className="text-success small">
                        Up to date <i className="bi bi-check-circle"></i>
                    </span>
                )}
                {saveStatus === "saving" && (
                    <span className="small">
                        Currently Saving <Spinner size="sm" />
                    </span>
                )}
                {saveStatus === "saved" && (
                    <span className="text-success small">
                        Saved <i className="bi bi-check-circle"></i>
                    </span>
                )}
                {saveStatus === "error" && (
                    <span className="text-danger small">
                        Save failed <i className="bi bi-x-circle"></i>
                    </span>
                )}
            </div>
            <RichTextEditor
                value={coerceToString(value)}
                onChange={handleChange}
                placeholder="Start typing..."
                minHeight={180}
                className="border m-3"
                ariaLabel="additionalInformation"
                readOnly={lockState && lockState?.isLocked}
            />
        </Card>
    );
}